import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Mail, ChevronDown, User, LogOut } from 'lucide-react';
import Button from '../common/Button';
import AuthModal from '../auth/AuthModal';
import LoginForm from '../auth/LoginForm';
import RegisterForm from '../auth/RegisterForm';
import { useAuth } from '../../contexts/AuthContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSignOut = async () => {
    await signOut();
    setShowUserDropdown(false);
    navigate('/');
  };

  const productItems = [
    { name: 'Email Marketing', path: '/product', hash: '#email-marketing' },
    { name: 'SMS Marketing', path: '/product', hash: '#sms-marketing' },
    { name: 'WhatsApp Marketing', path: '/product', hash: '#whatsapp-marketing' },
    { name: 'Social Media Marketing', path: '/product', hash: '#social-media' },
    { name: 'Online Advertising', path: '/product', hash: '#advertising' },
    { name: 'SEO Services', path: '/product', hash: '#seo' },
    { name: 'KOL Marketing', path: '/product', hash: '#kol' },
    { name: 'Business Email', path: '/product', hash: '#business-email' },
    { name: 'Website Builder', path: '/product', hash: '#website-builder' },
    { name: 'CRM System', path: '/product', hash: '#crm' }
  ];

  return (
    <>
      <nav 
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link 
                to="/" 
                className="flex-shrink-0 flex items-center cursor-pointer"
                onClick={scrollToTop}
              >
                <Mail className="h-8 w-8 text-blue-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">CustMax</span>
              </Link>
            </div>

            {/* Desktop menu */}
            <div className="hidden md:flex items-center space-x-6">
              <div className="relative">
                <Link
                  to="/product"
                  className="flex items-center text-gray-700 hover:text-blue-600 transition-colors"
                  onMouseEnter={() => setShowProductDropdown(true)}
                >
                  Product
                  <ChevronDown className="ml-1 h-4 w-4" />
                </Link>
                
                {showProductDropdown && (
                  <div
                    className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg py-2"
                    onMouseLeave={() => setShowProductDropdown(false)}
                  >
                    {productItems.map((item, index) => (
                      <Link
                        key={index}
                        to={`${item.path}${item.hash}`}
                        className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        onClick={() => setShowProductDropdown(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/solutions" className="text-gray-700 hover:text-blue-600 transition-colors">
                Solutions
              </Link>
              <Link to="/pricing" className="text-gray-700 hover:text-blue-600 transition-colors">
                Pricing
              </Link>
              <Link to="/resources" className="text-gray-700 hover:text-blue-600 transition-colors">
                Resources
              </Link>
              {user ? (
                <div className="relative">
                  <button
                    className="flex items-center text-gray-700 hover:text-blue-600 transition-colors"
                    onMouseEnter={() => setShowUserDropdown(true)}
                  >
                    <User className="h-5 w-5 mr-1" />
                    {user.email}
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>
                  {showUserDropdown && (
                    <div
                      className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2"
                      onMouseLeave={() => setShowUserDropdown(false)}
                    >
                      <Link
                        to="/dashboard"
                        className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        onClick={() => setShowUserDropdown(false)}
                      >
                        Dashboard
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowLoginModal(true)}
                  >
                    Log In
                  </Button>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => setShowRegisterModal(true)}
                  >
                    Try Free
                  </Button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 focus:outline-none"
              >
                {isOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`md:hidden ${isOpen ? 'block' : 'hidden'}`}>
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/product"
              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md"
              onClick={() => setIsOpen(false)}
            >
              Product
            </Link>
            <Link
              to="/solutions"
              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md"
              onClick={() => setIsOpen(false)}
            >
              Solutions
            </Link>
            <Link
              to="/pricing"
              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md"
              onClick={() => setIsOpen(false)}
            >
              Pricing
            </Link>
            <Link
              to="/resources"
              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md"
              onClick={() => setIsOpen(false)}
            >
              Resources
            </Link>
          </div>
          <div className="px-3 py-2 space-y-2">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="block w-full text-center px-4 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                  onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </Link>
                <Button 
                  variant="outline" 
                  size="sm" 
                  fullWidth
                  onClick={() => {
                    setIsOpen(false);
                    handleSignOut();
                  }}
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  fullWidth
                  onClick={() => {
                    setIsOpen(false);
                    setShowLoginModal(true);
                  }}
                >
                  Log In
                </Button>
                <Button 
                  variant="primary" 
                  size="sm" 
                  fullWidth
                  onClick={() => {
                    setIsOpen(false);
                    setShowRegisterModal(true);
                  }}
                >
                  Try Free
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Auth Modals */}
      <AuthModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        title="Welcome Back"
      >
        <LoginForm onClose={() => setShowLoginModal(false)} />
      </AuthModal>

      <AuthModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        title="Create Your Account"
      >
        <RegisterForm onClose={() => setShowRegisterModal(false)} />
      </AuthModal>
    </>
  );
};

export default Navbar;